import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";

const wrapStyle = { background: "#050505", minHeight: "100vh" };
const containerStyle = "max-w-3xl mx-auto px-6 py-16";
const h1Style = {
  fontFamily: "Unbounded, sans-serif",
  fontWeight: 900,
  fontSize: "clamp(1.8rem, 4vw, 3rem)",
  color: "#FFFFFF",
  textTransform: "uppercase",
  letterSpacing: "-0.02em",
  marginBottom: "0.5rem",
};
const taglineStyle = {
  color: "#FF005A",
  fontFamily: "Unbounded, sans-serif",
  fontSize: "0.65rem",
  letterSpacing: "0.25em",
  textTransform: "uppercase",
  marginBottom: "2rem",
};
const h2Style = {
  fontFamily: "Unbounded, sans-serif",
  fontWeight: 700,
  fontSize: "1.05rem",
  color: "#FFFFFF",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginTop: "2rem",
  marginBottom: "0.8rem",
};
const pStyle = {
  color: "#A1A1AA",
  fontFamily: "Manrope, sans-serif",
  fontSize: "0.95rem",
  lineHeight: 1.8,
  marginBottom: "1rem",
};
const liStyle = { ...pStyle, marginBottom: "0.4rem" };

function Layout({ tagline, title, children, testId, seoTitle, seoDesc }) {
  return (
    <div style={wrapStyle} data-testid={testId}>
      <SEO title={seoTitle} description={seoDesc} />
      <Navbar />
      <div style={{ paddingTop: "5rem" }}>
        <div className={containerStyle}>
          <p style={taglineStyle}>★ {tagline} ★</p>
          <h1 style={h1Style}>{title}</h1>
          <div style={{ height: 2, width: 60, background: "#FF005A", marginBottom: "2rem" }} />
          {children}
        </div>
      </div>
      <Footer />
    </div>
  );
}

/* ── Company facts verified via ARES (ares.gov.cz), Nov 2026 ── */
const SELLER = {
  name: "SOLAR GREEN s.r.o.",
  ico: "14222175",
  dic: "CZ14222175",
  address: "Na Folimance 2155/15, Vinohrady, 120 00 Praha 2",
  register: "C 362272 vedená u Městského soudu v Praze",
  founded: "4. 2. 2022",
  dataBox: "6u2bytn",
  vatPayer: true,
  email: "info@solar-green.cz",
  phone: "+420 604 943 419",
  shopUrl: "https://rebelky.online",
};

/* Pinkofein s.r.o. — ARES IČO 19747888; „Produkce Pinkofein“ = obchodní označení */
const SUPPLIER = {
  display: "Produkce Pinkofein s.r.o.",
  legal: "Pinkofein s.r.o.",
  ico: "19747888",
  dic: "CZ19747888",
  address: "Komenského 329, 250 75 Káraný",
  register: "C 391004 vedená u Městského soudu v Praze",
  vatNote: "neplátce DPH (ARES: registrace DPH zaniklá)",
};

const TERMS_COPY = {
  cz: {
    tagline: "Pravidla nákupu",
    title: "Obchodní podmínky",
    seoTitle: "Obchodní podmínky | Rebelky",
    seoDesc: "Všeobecné obchodní podmínky e-shopu Rebelky – SOLAR GREEN s.r.o.",
    intro: (
      <>
        Tyto všeobecné obchodní podmínky (dále jen „VOP“) upravují práva a povinnosti mezi
        prodávajícím – provozovatelem e-shopu <b style={{ color: "#fff" }}>rebelky.online</b> –
        a kupujícím při nákupu zboží (merch kapely Rebelky) na dálku. VOP jsou vyhotoveny
        v souladu s právním řádem České republiky, zejména zákonem č. 89/2012 Sb., občanský
        zákoník, a zákonem č. 634/1992 Sb., o ochraně spotřebitele.
      </>
    ),
    s1: "1. Identifikace prodávajícího a dodavatele",
    s1p1: (
      <>
        <b style={{ color: "#fff" }}>Prodávající / provozovatel e-shopu:</b>{" "}
        {SELLER.name}
        <br />
        IČO: {SELLER.ico}
        <br />
        DIČ: {SELLER.dic} (plátce DPH)
        <br />
        Sídlo: {SELLER.address}
        <br />
        Zápis v OR: {SELLER.register}
        <br />
        Datum vzniku: {SELLER.founded}
        <br />
        Datová schránka: {SELLER.dataBox}
        <br />
        E-mail:{" "}
        <a style={{ color: "#FF005A" }} href={`mailto:${SELLER.email}`}>{SELLER.email}</a>
        <br />
        Telefon:{" "}
        <a style={{ color: "#FF005A" }} href={`tel:${SELLER.phone.replace(/\s/g, "")}`}>{SELLER.phone}</a>
        <br />
        Web: {SELLER.shopUrl}
      </>
    ),
    s1p2: (
      <>
        <b style={{ color: "#fff" }}>Dodavatel produktů:</b> {SUPPLIER.display}
        {" "}(právní subjekt: <b style={{ color: "#fff" }}>{SUPPLIER.legal}</b>),
        IČO {SUPPLIER.ico}, DIČ {SUPPLIER.dic}, sídlo {SUPPLIER.address},
        zápis v OR: {SUPPLIER.register}
        {" "}— {SUPPLIER.vatNote}. Dodavatel zajišťuje zboží; smluvní vztah kupujícího vzniká
        s prodávajícím (provozovatelem e-shopu).
      </>
    ),
    s2: "2. Vymezení pojmů",
    s2items: [
      <>
        <b style={{ color: "#fff" }}>Kupující</b> — fyzická nebo právnická osoba objednávající zboží.
        Je-li kupující spotřebitel (FO nepodnikající), mají přednost ustanovení na ochranu spotřebitele.
      </>,
      <>
        <b style={{ color: "#fff" }}>Spotřebitel</b> — fyzická osoba, která mimo rámec podnikatelské
        činnosti uzavírá smlouvu s podnikatelem.
      </>,
      <>
        <b style={{ color: "#fff" }}>Zboží</b> — merchandising a související produkty nabízené v e-shopu
        (trička, plakáty, vinyl, podpis / věnování apod.).
      </>,
      <>
        <b style={{ color: "#fff" }}>Objednávka</b> — návrh na uzavření kupní smlouvy odeslaný přes pokladnu e-shopu.
      </>,
    ],
    s3: "3. Objednávka a uzavření smlouvy",
    s3p1: (
      <>
        Prezentace zboží v e-shopu je informativní. Kupní smlouva vzniká odesláním objednávky
        kupujícím a jejím potvrzením prodávajícím (e-mailové potvrzení objednávky). Prodávající
        může požádat o dodatečné potvrzení (e-mail / telefon).
      </>
    ),
    s3p2: (
      <>
        Odesláním objednávky kupující potvrzuje, že se seznámil s těmito VOP, se zásadami
        ochrany osobních údajů (<a style={{ color: "#FF005A" }} href="/gdpr">GDPR</a>) a s informacemi
        o <a style={{ color: "#FF005A" }} href="/doruceni">doručení a platbě</a>.
      </>
    ),
    s3p3: (
      <>
        Objednávka je evidována pod číslem objednávky. Smluvní jazyk je čeština; anglická
        verze VOP je informativní překlad.
      </>
    ),
    s4: "4. Cena zboží a poplatky",
    s4p1: (
      <>
        Ceny v e-shopu jsou uvedeny v CZK včetně všech daní a poplatků, které je prodávající
        povinen uvést. Cena dopravy, případný příplatek za dobírku / kartu na výdejně a
        volitelné služby (např. podpis) se zobrazí na pokladně před odesláním objednávky
        a jsou součástí celkové ceny.
      </>
    ),
    s4p2: (
      <>
        Prodávající si vyhrazuje právo opravit zjevnou chybu v ceně (např. technická chyba)
        před potvrzením smlouvy; kupujícího o tom informuje a nabídne mu zrušení objednávky.
      </>
    ),
    s5: "5. Platební podmínky",
    s5intro: "Podle zvolené dopravy může být na pokladně dostupná jedna či více z těchto metod:",
    s5items: [
      <>
        <b style={{ color: "#fff" }}>Karta online</b> (Visa / Mastercard / Apple Pay) přes platební
        bránu Stripe — platba ihned při dokončení objednávky.
      </>,
      <>
        <b style={{ color: "#fff" }}>QR platba / bankovní převod</b> v CZK — po objednávce e-mail
        s QR, IBAN a PDF. Variabilní symbol = číslo objednávky.
      </>,
      <>
        <b style={{ color: "#fff" }}>Dobírka</b> přes Zásilkovnu (příplatek dle aktuální nabídky).
      </>,
      <>
        <b style={{ color: "#fff" }}>Karta na výdejně</b> Zásilkovny, nebo <b style={{ color: "#fff" }}>hotově při osobním odběru</b>.
      </>,
    ],
    s5p2: (
      <>
        Zboží zůstává ve vlastnictví prodávajícího do úplného zaplacení kupní ceny
        (u předplatby / karty / převodu). U dobírky a platby při převzetí je cena splatná
        při předání zboží.
      </>
    ),
    s6: "6. Dodací podmínky",
    s6p1: (
      <>
        Zboží zasíláme po ČR zejména přes <b style={{ color: "#fff" }}>Zásilkovnu</b>, případně{" "}
        <b style={{ color: "#fff" }}>PPL</b> na adresu, nebo formou <b style={{ color: "#fff" }}>osobního odběru</b>.
        Aktuální ceny a možnosti viz{" "}
        <a style={{ color: "#FF005A" }} href="/doruceni">Doručení &amp; platba</a>.
      </>
    ),
    s6p2: (
      <>
        <b style={{ color: "#fff" }}>Termín dodání je cca 1 týden</b> — objednávky odesíláme{" "}
        <b style={{ color: "#fff" }}>1× týdně, každou středu</b> (po uhrazení, je-li vyžadována
        platba předem). O odeslání informujeme e-mailem / SMS. Nebezpečí škody na věci přechází
        na kupujícího převzetím zboží od dopravce / na výdejně.
      </>
    ),
    s7: "7. Odstoupení od smlouvy (spotřebitel)",
    s7p1: (
      <>
        Spotřebitel má právo odstoupit od smlouvy uzavřené na dálku bez udání důvodu
        v souladu s § 1829 a násl. občanského zákoníku ve lhůtě{" "}
        <b style={{ color: "#fff" }}>14 dnů</b> ode dne převzetí zboží.
      </>
    ),
    s7p2: (
      <>
        Odstoupení lze učinit jakýmkoli jednoznačným prohlášením (např. e-mailem na{" "}
        <a style={{ color: "#FF005A" }} href={`mailto:${SELLER.email}`}>{SELLER.email}</a>
        ) s uvedením jména, čísla objednávky a že odstupujete od smlouvy. Vzorový text:
        „Odstupuji od kupní smlouvy č. [číslo objednávky] uzavřené dne [datum], zboží vrátím.“
      </>
    ),
    s7p3: (
      <>
        Po odstoupení spotřebitel zašle zboží bez zbytečného odkladu, nejpozději do 14 dnů,
        na adresu sdělenou prodávajícím. Náklady na vrácení nese spotřebitel. Zboží by mělo
        být vráceno ideálně v původním stavu a obalu; spotřebitel odpovídá za snížení hodnoty
        zboží způsobené nakládáním s ním jinak, než je nutné k seznámení se s povahou a funkčností.
      </>
    ),
    s7p4: (
      <>
        Prodávající vrátí přijaté peněžní prostředky včetně nákladů na nejlevnější nabízenou
        dopravu nejpozději do <b style={{ color: "#fff" }}>14 dnů</b> od odstoupení; může vyčkat
        na doručení vráceného zboží nebo prokázání jeho odeslání. Vrácení plateb kartou probíhá
        stejným způsobem, pokud se strany nedohodnou jinak.
      </>
    ),
    s7p5: (
      <>
        Právo odstoupit <b style={{ color: "#fff" }}>nevzniká</b> zejména u zboží upraveného
        podle přání spotřebitele nebo pro jeho osobu (§ 1837 písm. d) OZ) — např. merch
        s dedicací / podpisem na jméno — a v dalších případech stanovených zákonem.
      </>
    ),
    s8: "8. Práva z vadného plnění a reklamace",
    s8p1: (
      <>
        Prodávající odpovídá spotřebiteli, že věc při převzetí nemá vady. Spotřebitel může
        uplatnit práva z vad, které se vyskytnou v době <b style={{ color: "#fff" }}>24 měsíců</b>{" "}
        od převzetí (zákonná lhůta u spotřebitelských koupí movitých věcí).
      </>
    ),
    s8p2: (
      <>
        Reklamaci uplatněte e-mailem na{" "}
        <a style={{ color: "#FF005A" }} href={`mailto:${SELLER.email}`}>{SELLER.email}</a>
        {" "}— uveďte číslo objednávky, popis vady a ideálně fotografie. Prodávající reklamaci
        vyřídí bez zbytečného odkladu, nejpozději do 30 dnů, nedohodnete-li se na delší lhůtě.
      </>
    ),
    s8p3: (
      <>
        Podle povahy vady má spotřebitel zejména právo na odstranění vady, dodání nové věci,
        přiměřenou slevu, nebo odstoupení od smlouvy — v rozsahu dle občanského zákoníku
        a zákona o ochraně spotřebitele.
      </>
    ),
    s9: "9. Ochrana osobních údajů",
    s9p: (
      <>
        Zpracování osobních údajů upravují{" "}
        <a style={{ color: "#FF005A" }} href="/gdpr">Zásady GDPR</a>. Správcem je provozovatel
        e-shopu ({SELLER.name}). Údaje zpracováváme zejména za účelem plnění smlouvy a zákonných
        povinností.
      </>
    ),
    s10: "10. Mimosoudní řešení sporů",
    s10p: (
      <>
        Spotřebitel má právo na mimosoudní řešení spotřebitelského sporu z kupní smlouvy.
        Subjektem mimosoudního řešení je <b style={{ color: "#fff" }}>Česká obchodní inspekce</b>{" "}
        (www.coi.cz). On-line platforma Evropské komise:{" "}
        <a style={{ color: "#FF005A" }} href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
          ec.europa.eu/consumers/odr
        </a>.
        Případné soudní spory řeší příslušné soudy České republiky.
      </>
    ),
    s11: "11. Závěrečná ustanovení",
    s11p1: (
      <>
        Vztahy neupravené těmito VOP se řídí právním řádem ČR. Neplatnost jednotlivého
        ustanovení nemá vliv na platnost ostatních. Prodávající může VOP změnit; rozhodující
        je verze platná v okamžiku odeslání objednávky, dostupná na {SELLER.shopUrl}.
      </>
    ),
    s11p2: (
      <>
        Tyto VOP nabývají účinnosti dnem zveřejnění na webu.
      </>
    ),
  },
  en: {
    tagline: "Purchase rules",
    title: "Terms and conditions",
    seoTitle: "Terms and conditions | Rebelky",
    seoDesc: "Terms of the Rebelky e-shop – SOLAR GREEN s.r.o.",
    intro: (
      <>
        These terms and conditions (“Terms”) govern the relationship between the seller —
        operator of the <b style={{ color: "#fff" }}>rebelky.online</b> e-shop — and the buyer
        when purchasing goods (Rebelky merch) at a distance. The Terms follow the laws of the
        Czech Republic, in particular Act No. 89/2012 Coll. (Civil Code) and Act No. 634/1992 Coll.
        (Consumer Protection Act). The Czech version prevails.
      </>
    ),
    s1: "1. Seller and supplier identification",
    s1p1: (
      <>
        <b style={{ color: "#fff" }}>Seller / e-shop operator:</b>{" "}
        {SELLER.name}
        <br />
        Company ID (IČO): {SELLER.ico}
        <br />
        VAT ID (DIČ): {SELLER.dic} (VAT payer)
        <br />
        Registered office: {SELLER.address}
        <br />
        Commercial Register: {SELLER.register}
        <br />
        Incorporated: {SELLER.founded}
        <br />
        Data mailbox: {SELLER.dataBox}
        <br />
        Email:{" "}
        <a style={{ color: "#FF005A" }} href={`mailto:${SELLER.email}`}>{SELLER.email}</a>
        <br />
        Phone:{" "}
        <a style={{ color: "#FF005A" }} href={`tel:${SELLER.phone.replace(/\s/g, "")}`}>{SELLER.phone}</a>
        <br />
        Website: {SELLER.shopUrl}
      </>
    ),
    s1p2: (
      <>
        <b style={{ color: "#fff" }}>Product supplier:</b> {SUPPLIER.display}
        {" "}(legal entity: <b style={{ color: "#fff" }}>{SUPPLIER.legal}</b>),
        IČO {SUPPLIER.ico}, DIČ {SUPPLIER.dic}, address {SUPPLIER.address},
        Commercial Register: {SUPPLIER.register}
        {" "}— {SUPPLIER.vatNote}. The supplier provides the goods; the contract is with the seller
        (e-shop operator).
      </>
    ),
    s2: "2. Definitions",
    s2items: [
      <>
        <b style={{ color: "#fff" }}>Buyer</b> — a natural or legal person placing an order.
        If the buyer is a consumer, consumer-protection rules take precedence.
      </>,
      <>
        <b style={{ color: "#fff" }}>Consumer</b> — a natural person acting outside their trade or business.
      </>,
      <>
        <b style={{ color: "#fff" }}>Goods</b> — merch and related products offered in the shop
        (tees, posters, vinyl, personalised signature, etc.).
      </>,
      <>
        <b style={{ color: "#fff" }}>Order</b> — an offer to conclude a purchase contract via checkout.
      </>,
    ],
    s3: "3. Order and contract formation",
    s3p1: (
      <>
        Product listings are informative. A purchase contract is formed when the buyer submits
        an order and the seller confirms it (order confirmation email). The seller may ask for
        extra confirmation by email or phone.
      </>
    ),
    s3p2: (
      <>
        By submitting an order the buyer confirms they have read these Terms, the{" "}
        <a style={{ color: "#FF005A" }} href="/gdpr">privacy policy (GDPR)</a>, and{" "}
        <a style={{ color: "#FF005A" }} href="/doruceni">shipping &amp; payment</a> information.
      </>
    ),
    s3p3: (
      <>
        Orders are recorded under an order number. The contract language is Czech; this English
        text is an informative translation.
      </>
    ),
    s4: "4. Prices and fees",
    s4p1: (
      <>
        Prices are shown in CZK including all taxes and fees the seller must disclose. Shipping,
        cash-on-delivery / card-at-pickup surcharges and optional services (e.g. signature) appear
        at checkout before you submit the order and form part of the total price.
      </>
    ),
    s4p2: (
      <>
        The seller may correct an obvious price error before confirming the contract and will
        inform the buyer, who may cancel the order.
      </>
    ),
    s5: "5. Payment",
    s5intro: "Depending on shipping, checkout may offer one or more of:",
    s5items: [
      <>
        <b style={{ color: "#fff" }}>Card online</b> (Visa / Mastercard / Apple Pay) via Stripe —
        pay immediately when completing the order.
      </>,
      <>
        <b style={{ color: "#fff" }}>QR / bank transfer</b> in CZK — email with QR, IBAN and PDF after
        ordering. Variable symbol = order number.
      </>,
      <>
        <b style={{ color: "#fff" }}>Cash on delivery</b> via Packeta (surcharge as shown).
      </>,
      <>
        <b style={{ color: "#fff" }}>Card at Packeta pickup</b>, or <b style={{ color: "#fff" }}>cash at store pickup</b>.
      </>,
    ],
    s5p2: (
      <>
        Title to the goods remains with the seller until the price is paid in full (prepaid /
        card / transfer). For COD / pay-on-collection, payment is due on handover.
      </>
    ),
    s6: "6. Delivery",
    s6p1: (
      <>
        We ship in Czechia mainly via <b style={{ color: "#fff" }}>Packeta</b>, optionally{" "}
        <b style={{ color: "#fff" }}>PPL</b> to your address, or <b style={{ color: "#fff" }}>store pickup</b>.
        See{" "}
        <a style={{ color: "#FF005A" }} href="/doruceni">Shipping &amp; payment</a> for current options.
      </>
    ),
    s6p2: (
      <>
        <b style={{ color: "#fff" }}>Delivery takes about 1 week</b> — we ship{" "}
        <b style={{ color: "#fff" }}>once a week, every Wednesday</b> (after prepaid payment where required).
        We notify you by email / SMS when sent. Risk passes on collection from the carrier / pickup point.
      </>
    ),
    s7: "7. Withdrawal (consumers)",
    s7p1: (
      <>
        A consumer may withdraw from a distance contract without giving a reason within{" "}
        <b style={{ color: "#fff" }}>14 days</b> of receiving the goods (Civil Code § 1829 et seq.).
      </>
    ),
    s7p2: (
      <>
        Withdraw by any clear statement (e.g. email to{" "}
        <a style={{ color: "#FF005A" }} href={`mailto:${SELLER.email}`}>{SELLER.email}</a>
        ) stating your name, order number and that you withdraw. Sample: “I withdraw from purchase
        contract No. [order number] concluded on [date]; I will return the goods.”
      </>
    ),
    s7p3: (
      <>
        After withdrawal, return the goods without undue delay and no later than 14 days to the
        address we provide. Return shipping is at the consumer’s expense. Goods should preferably
        be returned unused and in original packaging; the consumer is liable for diminished value
        from handling beyond what is necessary to establish the nature and functioning of the goods.
      </>
    ),
    s7p4: (
      <>
        We refund all payments received, including the cost of the cheapest offered delivery,
        within <b style={{ color: "#fff" }}>14 days</b> of withdrawal; we may wait until we receive
        the goods or proof of dispatch. Card refunds use the same payment method unless agreed otherwise.
      </>
    ),
    s7p5: (
      <>
        Withdrawal does <b style={{ color: "#fff" }}>not</b> apply to goods made to the consumer’s
        specifications or clearly personalised (§ 1837(d) Civil Code) — e.g. merch with a named
        dedication / signature — and other statutory exceptions.
      </>
    ),
    s8: "8. Defects and complaints",
    s8p1: (
      <>
        The seller warrants that goods are free from defects on receipt. Consumers may claim
        defects arising within <b style={{ color: "#fff" }}>24 months</b> of receipt.
      </>
    ),
    s8p2: (
      <>
        Lodge a complaint by email to{" "}
        <a style={{ color: "#FF005A" }} href={`mailto:${SELLER.email}`}>{SELLER.email}</a>
        {" "}with the order number, defect description and photos if possible. We handle complaints
        without undue delay and within 30 days unless a longer period is agreed.
      </>
    ),
    s8p3: (
      <>
        Depending on the defect, the consumer may seek repair, replacement, a reasonable discount
        or withdrawal — as provided by the Civil Code and Consumer Protection Act.
      </>
    ),
    s9: "9. Personal data",
    s9p: (
      <>
        Processing of personal data is described in the{" "}
        <a style={{ color: "#FF005A" }} href="/gdpr">GDPR policy</a>. The controller is the e-shop
        operator ({SELLER.name}). We process data mainly to perform the contract and meet legal duties.
      </>
    ),
    s10: "10. Out-of-court dispute resolution",
    s10p: (
      <>
        Consumers may use out-of-court dispute resolution for purchase contracts. The competent
        body is the <b style={{ color: "#fff" }}>Czech Trade Inspection Authority (ČOI)</b>{" "}
        (www.coi.cz). EU ODR platform:{" "}
        <a style={{ color: "#FF005A" }} href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
          ec.europa.eu/consumers/odr
        </a>.
        Court disputes are heard by Czech courts.
      </>
    ),
    s11: "11. Final provisions",
    s11p1: (
      <>
        Matters not covered by these Terms are governed by Czech law. Invalidity of one clause
        does not affect the rest. The seller may amend the Terms; the version in force when the
        order is submitted (published at {SELLER.shopUrl}) applies.
      </>
    ),
    s11p2: (
      <>
        These Terms take effect on the date of publication on the website.
      </>
    ),
  },
};

export function TermsPage() {
  const { lang } = useLanguage();
  const c = TERMS_COPY[lang === "en" ? "en" : "cz"];

  return (
    <Layout
      tagline={c.tagline}
      title={c.title}
      testId="terms-page"
      seoTitle={c.seoTitle}
      seoDesc={c.seoDesc}
    >
      <p style={pStyle}>{c.intro}</p>

      <h2 style={h2Style}>{c.s1}</h2>
      <p style={pStyle}>{c.s1p1}</p>
      <p style={pStyle}>{c.s1p2}</p>

      <h2 style={h2Style}>{c.s2}</h2>
      <ul style={{ marginLeft: "1.2rem", listStyle: "disc" }}>
        {c.s2items.map((item, i) => (
          <li key={i} style={liStyle}>{item}</li>
        ))}
      </ul>

      <h2 style={h2Style}>{c.s3}</h2>
      <p style={pStyle}>{c.s3p1}</p>
      <p style={pStyle}>{c.s3p2}</p>
      <p style={pStyle}>{c.s3p3}</p>

      <h2 style={h2Style}>{c.s4}</h2>
      <p style={pStyle}>{c.s4p1}</p>
      <p style={pStyle}>{c.s4p2}</p>

      <h2 style={h2Style}>{c.s5}</h2>
      <p style={pStyle}>{c.s5intro}</p>
      <ul style={{ marginLeft: "1.2rem", listStyle: "disc" }}>
        {c.s5items.map((item, i) => (
          <li key={i} style={liStyle}>{item}</li>
        ))}
      </ul>
      <p style={pStyle}>{c.s5p2}</p>

      <h2 style={h2Style}>{c.s6}</h2>
      <p style={pStyle}>{c.s6p1}</p>
      <p style={pStyle}>{c.s6p2}</p>

      <h2 style={h2Style}>{c.s7}</h2>
      <p style={pStyle}>{c.s7p1}</p>
      <p style={pStyle}>{c.s7p2}</p>
      <p style={pStyle}>{c.s7p3}</p>
      <p style={pStyle}>{c.s7p4}</p>
      <p style={pStyle}>{c.s7p5}</p>

      <h2 style={h2Style}>{c.s8}</h2>
      <p style={pStyle}>{c.s8p1}</p>
      <p style={pStyle}>{c.s8p2}</p>
      <p style={pStyle}>{c.s8p3}</p>

      <h2 style={h2Style}>{c.s9}</h2>
      <p style={pStyle}>{c.s9p}</p>

      <h2 style={h2Style}>{c.s10}</h2>
      <p style={pStyle}>{c.s10p}</p>

      <h2 style={h2Style}>{c.s11}</h2>
      <p style={pStyle}>{c.s11p1}</p>
      <p style={pStyle}>{c.s11p2}</p>
    </Layout>
  );
}

const GDPR_COPY = {
  cz: {
    tagline: "Ochrana osobních údajů",
    title: "Zásady GDPR",
    seoTitle: "GDPR – ochrana osobních údajů | Rebelky",
    seoDesc: "Zásady zpracování osobních údajů na webu Rebelky.",
    intro: (
      <>
        Provozovatelem internetového obchodu rebelky.online a správcem osobních údajů je společnost
        <b style={{ color: "#fff" }}> {SELLER.name}</b>
        {" "}(IČO {SELLER.ico}, DIČ {SELLER.dic}, sídlo {SELLER.address},
        zápis v OR: {SELLER.register}), kontaktní e-mail
        <a style={{ color: "#FF005A" }} href={`mailto:${SELLER.email}`}> {SELLER.email}</a>.
        Dodavatel produktů ({SUPPLIER.display} / {SUPPLIER.legal}, IČO {SUPPLIER.ico})
        může zpracovávat údaje v rozsahu nutném pro expedici a fakturaci jako zpracovatel / příjemce.
      </>
    ),
  },
  en: {
    tagline: "Personal data protection",
    title: "GDPR policy",
    seoTitle: "GDPR – personal data | Rebelky",
    seoDesc: "How Rebelky processes personal data.",
    intro: (
      <>
        The operator of rebelky.online and the controller of personal data is
        <b style={{ color: "#fff" }}> {SELLER.name}</b>
        {" "}(Company ID {SELLER.ico}, VAT ID {SELLER.dic}, registered office {SELLER.address},
        Commercial Register: {SELLER.register}), email
        <a style={{ color: "#FF005A" }} href={`mailto:${SELLER.email}`}> {SELLER.email}</a>.
        The product supplier ({SUPPLIER.display} / {SUPPLIER.legal}, IČO {SUPPLIER.ico})
        may process data as needed for fulfilment and invoicing as a processor / recipient.
      </>
    ),
  },
};

export function GdprPage() {
  const { lang } = useLanguage();
  const isEn = lang === "en";
  const head = GDPR_COPY[isEn ? "en" : "cz"];

  return (
    <Layout
      tagline={head.tagline}
      title={head.title}
      testId="gdpr-page"
      seoTitle={head.seoTitle}
      seoDesc={head.seoDesc}
    >
      <p style={pStyle}>{head.intro}</p>

      <h2 style={h2Style}>{isEn ? "1. What data we process" : "1. Jaké údaje zpracováváme"}</h2>
      <ul style={{ marginLeft: "1.2rem", listStyle: "disc" }}>
        <li style={liStyle}>{isEn ? "name and surname" : "jméno a příjmení"}</li>
        <li style={liStyle}>{isEn ? "email address" : "e-mailová adresa"}</li>
        <li style={liStyle}>{isEn ? "phone number" : "telefonní číslo"}</li>
        <li style={liStyle}>{isEn ? "delivery address" : "doručovací adresa"}</li>
        <li style={liStyle}>{isEn ? "order and payment data" : "údaje o objednávce a platbě"}</li>
      </ul>

      <h2 style={h2Style}>{isEn ? "2. Purpose and legal basis" : "2. Účel a právní základ zpracování"}</h2>
      <p style={pStyle}>
        {isEn ? (
          <>
            We process personal data to <b style={{ color: "#fff" }}>perform the purchase contract</b>
            {" "}(Art. 6(1)(b) GDPR), to <b style={{ color: "#fff" }}>meet legal obligations</b>
            {" "}(invoices, tax records — Art. 6(1)(c) GDPR), and with explicit consent for{" "}
            <b style={{ color: "#fff" }}>marketing</b> (Art. 6(1)(a) GDPR).
          </>
        ) : (
          <>
            Osobní údaje zpracováváme za účelem <b style={{ color: "#fff" }}>plnění kupní smlouvy</b>
            {" "}(čl. 6 odst. 1 písm. b GDPR), <b style={{ color: "#fff" }}>splnění právních povinností</b>
            {" "}(vystavení faktur, daňová evidence – čl. 6 odst. 1 písm. c GDPR) a v případě výslovného souhlasu
            i pro <b style={{ color: "#fff" }}>marketingovou komunikaci</b> (čl. 6 odst. 1 písm. a GDPR).
          </>
        )}
      </p>

      <h2 style={h2Style}>{isEn ? "3. Retention" : "3. Doba uchování"}</h2>
      <p style={pStyle}>
        {isEn
          ? "We keep data as long as needed to fulfil the order and as required by law (especially 10 years for accounting/tax records)."
          : "Údaje uchováváme po dobu nezbytnou k vyřízení objednávky a po dobu stanovenou zákonem (zejména 10 let pro účetní a daňové doklady)."}
      </p>

      <h2 style={h2Style}>{isEn ? "4. Recipients" : "4. Předávání třetím stranám"}</h2>
      <p style={pStyle}>
        {isEn
          ? "We share data only as needed to fulfil orders — carriers (Packeta, PPL), payment providers (e.g. Stripe), email providers, and the product supplier. We do not sell personal data."
          : "Osobní údaje předáváme pouze v rozsahu nutném k vyřízení objednávky — přepravcům (Zásilkovna, PPL), platebním poskytovatelům (např. Stripe), e-mailovým službám a dodavateli produktů. Údaje neprodáváme."}
      </p>

      <h2 style={h2Style}>{isEn ? "5. Your rights" : "5. Vaše práva"}</h2>
      <ul style={{ marginLeft: "1.2rem", listStyle: "disc" }}>
        <li style={liStyle}>{isEn ? "access to your data" : "právo na přístup ke svým údajům"}</li>
        <li style={liStyle}>{isEn ? "rectification" : "právo na opravu nepřesných údajů"}</li>
        <li style={liStyle}>{isEn ? "erasure (“right to be forgotten”)" : "právo na výmaz („právo být zapomenut“)"}</li>
        <li style={liStyle}>{isEn ? "restriction of processing" : "právo na omezení zpracování"}</li>
        <li style={liStyle}>{isEn ? "data portability" : "právo na přenositelnost údajů"}</li>
        <li style={liStyle}>{isEn ? "objection to processing" : "právo vznést námitku proti zpracování"}</li>
        <li style={liStyle}>
          {isEn
            ? "complaint to the Czech Data Protection Authority (uoou.cz)"
            : "právo podat stížnost u Úřadu pro ochranu osobních údajů (uoou.cz)"}
        </li>
      </ul>

      <h2 style={h2Style}>{isEn ? "6. Contact" : "6. Kontakt"}</h2>
      <p style={pStyle}>
        {isEn ? "To exercise your rights or ask about privacy, email" : "Pro uplatnění práv či dotazy k ochraně osobních údajů pište na"}
        <a style={{ color: "#FF005A" }} href={`mailto:${SELLER.email}`}> {SELLER.email}</a>.
        {isEn ? " We reply within 30 days." : " Odpovíme nejpozději do 30 dnů."}
      </p>
    </Layout>
  );
}

const SHIPPING_COPY = {
  cz: {
    tagline: "Doručení a platba",
    title: "Doručení & platba",
    seoTitle: "Doručení a platba | Rebelky",
    seoDesc: "Informace o doručení, platbě, provozovateli e-shopu a termínu dodání.",
    operators: "Provozovatel a dodavatel",
    operatorsP1: (
      <>
        Eshop provozuje <b style={{ color: "#fff" }}>SOLAR GREEN s.r.o.</b>
      </>
    ),
    operatorsP2: (
      <>
        Dodavatel produktů je <b style={{ color: "#fff" }}>Produkce Pinkofein s.r.o.</b>
        {" "}({SUPPLIER.legal}, IČO {SUPPLIER.ico}).
      </>
    ),
    payment: "Způsob platby",
    paymentIntro:
      "Na pokladně si vybereš jednu z dostupných platebních metod (podle zvolené dopravy):",
    paymentMethods: [
      <>
        <b style={{ color: "#fff" }}>Karta online</b> (Visa / Mastercard / Apple Pay) přes Stripe — zaplatíš ihned na bezpečné platební bráně.
      </>,
      <>
        <b style={{ color: "#fff" }}>QR platba / bankovní převod</b> v CZK — po objednávce dostaneš e-mail s QR, IBAN a PDF. Variabilní symbol = číslo objednávky.
      </>,
      <>
        <b style={{ color: "#fff" }}>Dobírka</b> přes Zásilkovnu — zaplatíš hotově při vyzvednutí na výdejně (může být příplatek).
      </>,
      <>
        <b style={{ color: "#fff" }}>Karta na výdejně</b> Zásilkovny — zaplatíš kartou při vyzvednutí.
      </>,
      <>
        <b style={{ color: "#fff" }}>Hotově při osobním odběru</b> — pouze u osobního odběru.
      </>,
    ],
    paymentNote:
      "Přesná nabídka plateb na pokladně závisí na zvolené dopravě a aktuálním nastavení e-shopu. Podrobnosti ve VOP.",
    shipping: "Doprava",
    shippingP: (
      <>
        Zboží zasíláme po celé ČR přes <b style={{ color: "#fff" }}>Zásilkovnu</b>
        {" "}nebo <b style={{ color: "#fff" }}>PPL</b> na adresu. K dispozici je i{" "}
        <b style={{ color: "#fff" }}>osobní odběr</b>. Cena dopravy se zobrazí na pokladně
        (Zásilkovna obvykle <b style={{ color: "#fff" }}>99 Kč</b>).
      </>
    ),
    delivery: "Termín dodání",
    deliveryP1: (
      <>
        Termín dodání je <b style={{ color: "#fff" }}>1 týden</b> — objednávky odesíláme{" "}
        <b style={{ color: "#fff" }}>1× týdně, každou středu</b>.
      </>
    ),
    deliveryP2:
      "O odeslání zásilky tě budeme informovat e-mailem (a případně SMS). V případě expresu nás kontaktuj telefonicky.",
    cancel: "Storno a vrácení",
    cancelP: (
      <>
        Objednávku můžeš stornovat e-mailem na
        <a style={{ color: "#FF005A" }} href={`mailto:${SELLER.email}`}> {SELLER.email}</a>
        do doby expedice. Po doručení má spotřebitel dle zákona 14 dnů na odstoupení (viz
        <a style={{ color: "#FF005A" }} href="/obchodni-podminky"> Obchodní podmínky</a>).
      </>
    ),
    contact: "Kontakt",
    contactP: (
      <>
        <a style={{ color: "#FF005A" }} href={`tel:${SELLER.phone.replace(/\s/g, "")}`}>{SELLER.phone}</a> ·
        <a style={{ color: "#FF005A" }} href={`mailto:${SELLER.email}`}> {SELLER.email}</a>
      </>
    ),
  },
  en: {
    tagline: "Shipping and payment",
    title: "Shipping & payment",
    seoTitle: "Shipping and payment | Rebelky",
    seoDesc: "Shipping, payment methods, shop operator, and delivery timing.",
    operators: "Operator and supplier",
    operatorsP1: (
      <>
        The e-shop is operated by <b style={{ color: "#fff" }}>SOLAR GREEN s.r.o.</b>
      </>
    ),
    operatorsP2: (
      <>
        The product supplier is <b style={{ color: "#fff" }}>Produkce Pinkofein s.r.o.</b>
        {" "}({SUPPLIER.legal}, IČO {SUPPLIER.ico}).
      </>
    ),
    payment: "Payment methods",
    paymentIntro:
      "At checkout you choose one of the available payment methods (depending on shipping):",
    paymentMethods: [
      <>
        <b style={{ color: "#fff" }}>Card online</b> (Visa / Mastercard / Apple Pay) via Stripe — pay immediately on a secure checkout page.
      </>,
      <>
        <b style={{ color: "#fff" }}>QR payment / bank transfer</b> in CZK — after ordering you get an email with QR, IBAN and PDF. Variable symbol = order number.
      </>,
      <>
        <b style={{ color: "#fff" }}>Cash on delivery</b> via Packeta — pay in cash when picking up (a fee may apply).
      </>,
      <>
        <b style={{ color: "#fff" }}>Card at Packeta pickup</b> — pay by card when collecting the parcel.
      </>,
      <>
        <b style={{ color: "#fff" }}>Cash at store pickup</b> — only for personal pickup.
      </>,
    ],
    paymentNote:
      "Exact payment options depend on shipping and shop settings. See the Terms for details.",
    shipping: "Shipping",
    shippingP: (
      <>
        We ship across Czechia via <b style={{ color: "#fff" }}>Packeta</b>
        {" "}or <b style={{ color: "#fff" }}>PPL</b> to your address.{" "}
        <b style={{ color: "#fff" }}>Store pickup</b> is also available. Shipping price is shown at checkout
        (Packeta is usually <b style={{ color: "#fff" }}>99 CZK</b>).
      </>
    ),
    delivery: "Delivery time",
    deliveryP1: (
      <>
        Delivery takes about <b style={{ color: "#fff" }}>1 week</b> — we ship orders{" "}
        <b style={{ color: "#fff" }}>once a week, every Wednesday</b>.
      </>
    ),
    deliveryP2:
      "We will email you (and may SMS) when the parcel is sent. For express shipping, contact us by phone.",
    cancel: "Cancellation and returns",
    cancelP: (
      <>
        You can cancel by emailing
        <a style={{ color: "#FF005A" }} href={`mailto:${SELLER.email}`}> {SELLER.email}</a>
        before dispatch. After delivery consumers have 14 days to withdraw (see
        <a style={{ color: "#FF005A" }} href="/obchodni-podminky"> Terms and conditions</a>).
      </>
    ),
    contact: "Contact",
    contactP: (
      <>
        <a style={{ color: "#FF005A" }} href={`tel:${SELLER.phone.replace(/\s/g, "")}`}>{SELLER.phone}</a> ·
        <a style={{ color: "#FF005A" }} href={`mailto:${SELLER.email}`}> {SELLER.email}</a>
      </>
    ),
  },
};

export function ShippingPage() {
  const { lang } = useLanguage();
  const c = SHIPPING_COPY[lang === "en" ? "en" : "cz"];

  return (
    <Layout
      tagline={c.tagline}
      title={c.title}
      testId="shipping-page"
      seoTitle={c.seoTitle}
      seoDesc={c.seoDesc}
    >
      <h2 style={h2Style}>{c.operators}</h2>
      <p style={pStyle}>{c.operatorsP1}</p>
      <p style={pStyle}>{c.operatorsP2}</p>

      <h2 style={h2Style}>{c.payment}</h2>
      <p style={pStyle}>{c.paymentIntro}</p>
      <ul style={{ marginLeft: "1.2rem", listStyle: "disc" }}>
        {c.paymentMethods.map((item, i) => (
          <li key={i} style={liStyle}>{item}</li>
        ))}
      </ul>
      <p style={pStyle}>{c.paymentNote}</p>

      <h2 style={h2Style}>{c.shipping}</h2>
      <p style={pStyle}>{c.shippingP}</p>

      <h2 style={h2Style}>{c.delivery}</h2>
      <p style={pStyle}>{c.deliveryP1}</p>
      <p style={pStyle}>{c.deliveryP2}</p>

      <h2 style={h2Style}>{c.cancel}</h2>
      <p style={pStyle}>{c.cancelP}</p>

      <h2 style={h2Style}>{c.contact}</h2>
      <p style={pStyle}>{c.contactP}</p>
    </Layout>
  );
}
